import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { CreditCard } from "lucide-react";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  buildFromEventApplicantProfileBreadcrumbs,
  formatHijriDisplayDate,
} from "../../../../utils/Helpers";
import {
  UICopyButton,
  UIDocumentRow,
  UIEmptyState,
  UIInfoRow,
  UILanguageSkillBar,
  UIProfileCompleteness,
  UIStatusTag,
} from "../../../../components/ui";
import {
  clearCompanyApplicantProfile,
  getCompanyApplicantProfileRequest,
} from "../../../../store/CompanyPortal/ApplicantProfile/actions";
import {
  computeProfileCompleteness,
  formatProfileDate,
  getApplicantAge,
  getCertificateDocuments,
  isExpiredDate,
  isSaudiNationality,
  readApplicantDisplayName,
  readUserLanguageName,
  readUserLanguageProficiencyLabel,
  resolveDocumentUrl,
} from "./profileHelpers";
import {
  CompanyWorkedWithRow,
  HealthBlock,
  ProfileSectionCard,
  ProfileSubsection,
} from "./ApplicantProfileLayout";
import ApplicantProfilePageHeader from "./ApplicantProfilePageHeader";
import "./ViewApplicantProfile.scss";

const formatNamedOptionList = (items) =>
  (items ?? [])
    .map((item) => item?.name)
    .filter(Boolean)
    .join(", ");

function ViewApplicantProfile({ portalVariant }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isAdminPortal = portalVariant === "admin-portal";
  const lang = useSelector((state) => state.locale.lang);
  const { profile, loading, notFound } = useSelector(
    (state) =>
      state.companyApplicantProfile ?? {
        profile: null,
        loading: false,
        notFound: false,
      },
  );
  const { applicants, events, shared } = messages[lang];
  const copy = applicants.companyApplicantProfile;
  const profileLabels = applicants.applicantDetails.applicantProfile.labels;
  const fieldLabels = copy.fieldLabels;
  const fromEvent = location.state?.fromEvent;
  const fromLoyalApplicants = Boolean(location.state?.fromLoyalApplicants);

  useEffect(() => {
    if (!id) return undefined;
    dispatch(
      getCompanyApplicantProfileRequest({
        id,
        portal: isAdminPortal ? "admin" : "company",
      }),
    );
    return () => {
      dispatch(clearCompanyApplicantProfile());
    };
  }, [dispatch, id, isAdminPortal, lang]);

  const displayName = readApplicantDisplayName(profile, lang);
  const isSaudi = isSaudiNationality(profile?.nationality);
  const certificates = profile ? getCertificateDocuments(profile) : [];
  const educationLabels = profileLabels.educationInfo;
  const basicLabels = profileLabels.basicInfo;
  const fieldOfStudyName = profile?.field_of_study?.name;
  const customFieldOfStudyName = profile?.field_of_study_name;
  const disabilityNames = formatNamedOptionList(profile?.disabilities);
  const chronicDiseaseNames = formatNamedOptionList(profile?.chronic_diseases);
  const yesLabel =
    shared.editRequestBanner?.cancelConfirmDialog?.yes || "Yes";
  const noLabel = shared.editRequestBanner?.cancelConfirmDialog?.no || "No";
  const notifiableLabel =
    profile?.is_notifiable === true
      ? yesLabel
      : profile?.is_notifiable === false
        ? noLabel
        : null;
  const hasUserLanguages = (profile?.user_languages?.length ?? 0) > 0;
  const hasPersonalInfoFields = Boolean(
    fieldOfStudyName || customFieldOfStudyName || notifiableLabel,
  );
  const showPersonalInfoSection = hasPersonalInfoFields || hasUserLanguages;
  const completeness = useMemo(
    () =>
      profile
        ? computeProfileCompleteness(profile, {
            basic: profileLabels.basicInfo,
            bank: profileLabels.bankInfo,
            education: profileLabels.educationInfo,
            age: fieldLabels.age,
            dateOfBirth: copy.dateOfBirth,
            gender: profileLabels.basicInfo.gender,
            companyWorkedWith: copy.companyWorkedWith,
            matloobNumber: fieldLabels.matloobNumber,
            certificates: profileLabels.documentInfo.certificates,
            academicQualifications:
              profileLabels.documentInfo.academicQualifications,
            cv: profileLabels.documentInfo.cv,
            iqamaExpired: copy.iqamaExpired,
          })
        : {
            percent: 0,
            missingCount: 0,
            missingFields: [],
            missingOptionalFields: [],
          },
    [copy, fieldLabels, profile, profileLabels],
  );

  const applicantsListPath = fromEvent?.eventId
    ? `${(isAdminPortal ? ROUTE_PATHS.viewEvent : ROUTE_PATHS.companyViewEvent).replace(":id", fromEvent.eventId)}?tab=applicants`
    : isAdminPortal
      ? ROUTE_PATHS.applicants
      : ROUTE_PATHS.companyApplicants;

  const loyalApplicantsListPath = ROUTE_PATHS.companyLoyalApplicants;

  const eventsListPath = isAdminPortal
    ? ROUTE_PATHS.events
    : ROUTE_PATHS.companyEvents;

  const eventViewPath = fromEvent?.eventId
    ? (isAdminPortal ? ROUTE_PATHS.viewEvent : ROUTE_PATHS.companyViewEvent).replace(
        ":id",
        fromEvent.eventId,
      )
    : null;

  const breadcrumbList = fromEvent?.eventId
    ? buildFromEventApplicantProfileBreadcrumbs({
        fromEvent,
        eventsListPath,
        eventsMessages: events,
        applicantName: displayName || copy.profileTitle,
        eventViewPath,
      })
    : fromLoyalApplicants
      ? [
          {
            label: applicants.loyalApplicants.breadcrumb,
            path: loyalApplicantsListPath,
          },
          { label: displayName || copy.profileTitle, path: "" },
        ]
      : [
          {
            label: isAdminPortal
              ? applicants.listApplicants.listApplicantsBreadcrumb
              : copy.breadcrumbApplicants,
            path: applicantsListPath,
          },
          { label: displayName || copy.profileTitle, path: "" },
        ];

  const profileBackPath = fromLoyalApplicants
    ? loyalApplicantsListPath
    : applicantsListPath;

  if (notFound) {
    return (
      <Box className="company-applicant-profile">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
        <UIEmptyState
          title={copy.profileNotFound}
          description={copy.profileNotFoundDescription}
          actionLabel={copy.backToApplicants}
          onAction={() => history.push(profileBackPath)}
        />
      </Box>
    );
  }

  if (loading && !profile) {
    return (
      <Box className="company-applicant-profile">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
        <div className="company-applicant-profile__loading" aria-live="polite">
          {shared.loading}
        </div>
      </Box>
    );
  }

  return (
    <Box className="company-applicant-profile">
      <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />

      <ApplicantProfilePageHeader
        applicantId={id}
        portalVariant={portalVariant}
        profile={profile}
        showEditButton
      />

      <div className="company-applicant-profile__grid">
        <div className="company-applicant-profile__column">
          <ProfileSectionCard title={copy.contactPerson}>
            <ProfileSubsection title={copy.identity} divider={false}>
              <UIInfoRow
                label={fieldLabels.nameEn}
                value={profile?.full_name_en}
                className="company-applicant-profile__field"
              />
              <UIInfoRow
                label={fieldLabels.nameAr}
                value={profile?.full_name_ar}
                className="company-applicant-profile__field"
              />
              <UIInfoRow
                label={fieldLabels.nationality}
                value={profile?.nationality?.name}
                className="company-applicant-profile__field"
              />
              <UIInfoRow
                label={fieldLabels.age}
                value={getApplicantAge(profile)}
                className="company-applicant-profile__field"
              />
              {isAdminPortal ? (
                <>
                  <UIInfoRow
                    label={fieldLabels.email}
                    value={profile?.email}
                    ltr
                    className="company-applicant-profile__field"
                  />
                  <UIInfoRow
                    label={fieldLabels.phoneNumber}
                    value={
                      profile?.phone_number
                        ? `${profile?.country_code || ""}${profile.phone_number}`
                        : profile?.phone_number
                    }
                    ltr
                    className="company-applicant-profile__field"
                  />
                </>
              ) : null}
              {isSaudi ? (
                <UIInfoRow
                  label={fieldLabels.nationalId}
                  value={profile?.national_id}
                  ltr
                  className="company-applicant-profile__field"
                />
              ) : null}
              {isAdminPortal ? (
                <UIInfoRow
                  label={fieldLabels.stcNumber}
                  value={profile?.stc_number}
                  ltr
                  className="company-applicant-profile__field"
                />
              ) : null}
            </ProfileSubsection>

            <ProfileSubsection title={copy.dateOfBirth}>
              <UIInfoRow
                label={fieldLabels.gregorian}
                value={formatProfileDate(profile?.gregorian_date_of_birth, lang)}
                className="company-applicant-profile__field"
              />
              <UIInfoRow
                label={fieldLabels.hijri}
                value={formatHijriDisplayDate(profile?.hijri_date_of_birth, lang)}
                className="company-applicant-profile__field"
              />
            </ProfileSubsection>

            <ProfileSubsection title={copy.physicalDetails}>
              <UIInfoRow
                label={fieldLabels.weight}
                value={
                  profile?.weight != null ? `${profile.weight} ${shared.kg}` : null
                }
                className="company-applicant-profile__field"
              />
              <UIInfoRow
                label={fieldLabels.height}
                value={
                  profile?.height != null ? `${profile.height} ${shared.cm}` : null
                }
                className="company-applicant-profile__field"
              />
            </ProfileSubsection>

            {!isSaudi ? (
              <ProfileSubsection title={copy.residencyId}>
                <UIInfoRow
                  label={fieldLabels.iqamaNumber}
                  value={profile?.iqama_id}
                  ltr
                  className="company-applicant-profile__field"
                />
                <UIInfoRow
                  label={fieldLabels.iqamaExpiry}
                  className="company-applicant-profile__field"
                >
                  {isExpiredDate(profile?.iqama_expiration_date) ? (
                    <div className="company-applicant-profile__expiry-value">
                      <span className="company-applicant-profile__expiry-date">
                        {formatProfileDate(profile?.iqama_expiration_date, lang)}
                      </span>
                      <UIStatusTag
                        preset="table"
                        status="canceled"
                        label={copy.expired}
                        className="company-applicant-profile__expired-tag"
                      />
                    </div>
                  ) : (
                    formatProfileDate(profile?.iqama_expiration_date, lang)
                  )}
                </UIInfoRow>
                <UIInfoRow
                  label={fieldLabels.matloobNumber}
                  value={profile?.matloob_number}
                  ltr
                  className="company-applicant-profile__field"
                />
              </ProfileSubsection>
            ) : null}

            <ProfileSubsection grid={false}>
              <CompanyWorkedWithRow label={copy.companyWorkedWith}>
                <span className="company-applicant-profile__company-value">
                  {profile?.favourite_company?.name || shared.none}
                </span>
              </CompanyWorkedWithRow>
            </ProfileSubsection>
          </ProfileSectionCard>

          <ProfileSectionCard title={copy.healthAccessibility}>
            <div className="company-applicant-profile__health">
              <HealthBlock
                title={copy.hasMedicalCondition}
                label={copy.medicalConditionDetails}
                value={chronicDiseaseNames || copy.noneReported}
              />
              <HealthBlock
                title={copy.hasDisability}
                label={copy.disabilityDetails}
                value={disabilityNames || copy.noneReported}
              />
            </div>
          </ProfileSectionCard>
        </div>

        <div className="company-applicant-profile__column">
          {completeness.missingCount > 0 ? (
            <ProfileSectionCard title={copy.profileCompleteness}>
              <UIProfileCompleteness
                percent={completeness.percent}
                missingFields={completeness.missingFields}
                completeLabel={copy.completeStatus}
                fieldsMissingLabel={copy.fieldsMissing.replace(
                  "{count}",
                  completeness.missingCount,
                )}
                missingFieldsTitle={copy.missingRequiredFields}
                missingOptionalFields={completeness.missingOptionalFields}
                missingOptionalFieldsTitle={copy.missingOptionalFields}
              />
            </ProfileSectionCard>
          ) : null}

          <ProfileSectionCard title={profileLabels.documentInfo.title}>
            <UIDocumentRow
              title={profileLabels.documentInfo.cv}
              subtitle={
                profile?.cv_documents?.[0]
                  ? copy.documentAvailable
                  : undefined
              }
              statusText={
                profile?.cv_documents?.[0] ? "" : copy.notProvided
              }
              href={resolveDocumentUrl(profile?.cv_documents?.[0])}
              downloadLabel={copy.downloadDocument}
            />
            <UIDocumentRow
              title={profileLabels.documentInfo.certificates}
              statusText={
                certificates.length
                  ? copy.documentAvailable
                  : copy.notProvided
              }
              href={resolveDocumentUrl(certificates[0])}
              downloadLabel={copy.downloadDocument}
            />
            <UIDocumentRow
              title={profileLabels.documentInfo.academicQualifications}
              statusText={
                profile?.academic_qualification_documents?.length
                  ? copy.documentAvailable
                  : copy.notProvided
              }
              href={resolveDocumentUrl(
                profile?.academic_qualification_documents?.[0],
              )}
              downloadLabel={copy.downloadDocument}
            />
            <UIDocumentRow
              title={profileLabels.documentInfo.other}
              statusText={
                profile?.other_documents?.length
                  ? copy.documentAvailable
                  : copy.notProvided
              }
              href={resolveDocumentUrl(profile?.other_documents?.[0])}
              downloadLabel={copy.downloadDocument}
            />
          </ProfileSectionCard>

          <ProfileSectionCard title={profileLabels.bankInfo.title}>
            <div className="company-applicant-profile__bank">
              <div className="company-applicant-profile__bank-box">
                <div className="company-applicant-profile__bank-icon-box" aria-hidden>
                  <CreditCard size={18} strokeWidth={1.75} />
                </div>
                <div className="company-applicant-profile__bank-copy">
                  <span className="company-applicant-profile__meta-label">
                    {profileLabels.bankInfo.bankName}
                  </span>
                  <span className="company-applicant-profile__meta-value">
                    {profile?.bank?.name || shared.none}
                  </span>
                </div>
              </div>

              <div className="company-applicant-profile__bank-field">
                <span className="company-applicant-profile__meta-label">
                  {profileLabels.bankInfo.IBAN}
                </span>
                <div className="company-applicant-profile__iban-box">
                  <span
                    dir="ltr"
                    className="company-applicant-profile__meta-value company-applicant-profile__meta-value--sm"
                  >
                    {profile?.iban || shared.none}
                  </span>
                  {profile?.iban ? (
                    <UICopyButton
                      value={profile.iban}
                      ariaLabel={copy.copyIban}
                      successMessage={copy.ibanCopied}
                    />
                  ) : null}
                </div>
              </div>

              <div className="company-applicant-profile__bank-field">
                <span className="company-applicant-profile__meta-label">
                  {profileLabels.bankInfo.cardHolder}
                </span>
                <span className="company-applicant-profile__meta-value">
                  {profile?.card_holder_name || shared.none}
                </span>
              </div>
            </div>
          </ProfileSectionCard>

          {showPersonalInfoSection ? (
            <ProfileSectionCard title={profileLabels.educationInfo.title}>
              {hasPersonalInfoFields ? (
                <div className="company-applicant-profile__field-grid">
                  {fieldOfStudyName ? (
                    <UIInfoRow
                      label={educationLabels.filedsOfStudy}
                      value={fieldOfStudyName}
                      className="company-applicant-profile__field"
                    />
                  ) : null}
                  {fieldOfStudyName === "Other" && customFieldOfStudyName ? (
                    <UIInfoRow
                      label={educationLabels.fieldOfStudyName}
                      value={customFieldOfStudyName}
                      className="company-applicant-profile__field"
                    />
                  ) : null}
                  {!fieldOfStudyName && customFieldOfStudyName ? (
                    <UIInfoRow
                      label={educationLabels.filedsOfStudy}
                      value={customFieldOfStudyName}
                      className="company-applicant-profile__field"
                    />
                  ) : null}
                  {notifiableLabel ? (
                    <UIInfoRow
                      label={basicLabels.notifiable}
                      value={notifiableLabel}
                      className="company-applicant-profile__field"
                    />
                  ) : null}
                </div>
              ) : null}

              {hasUserLanguages ? (
                <ProfileSubsection
                  title={profileLabels.languageInfo.title}
                  grid={false}
                  divider={hasPersonalInfoFields}
                >
                  <div className="company-applicant-profile__language-list">
                    {profile.user_languages.map((item) => (
                      <UILanguageSkillBar
                        key={item.id}
                        language={readUserLanguageName(item)}
                        proficiency={item.speaking_proficiency}
                        proficiencyLabel={readUserLanguageProficiencyLabel(
                          item,
                          lang,
                        )}
                      />
                    ))}
                  </div>
                </ProfileSubsection>
              ) : null}
            </ProfileSectionCard>
          ) : null}
        </div>
      </div>
    </Box>
  );
}

export default ViewApplicantProfile;

ViewApplicantProfile.propTypes = {
  portalVariant: PropTypes.oneOf(["admin-portal", "company-portal"]),
};

ViewApplicantProfile.defaultProps = {
  portalVariant: "company-portal",
};
